import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CrudStyles } from '../shared/Crud';
import DeliveryStatusBadge from '../Deliveries/DeliveryStatusBadge';
import DeliveryDriverSection from '../Deliveries/steps/DeliveryDriverSection';
import MessageModal from '../shared/MessageModal';
import deliveryService from '../../services/deliveryService';
import { ROUTE_PLANNING_DELIVERY_STATES } from '../Deliveries/statusConfig';
import routeService, {
    getPointDeliveryDetails,
    mapRouteDeliveryToPlanningShape,
} from '../../services/routeService';
import { getFriendlyErrorMessage } from '../../utils/errorHandler';
import {
    getDefaultDeliveryDateRange,
    MAX_DELIVERY_PAGE_SIZE,
    validateDeliveryDateRange,
} from '../../utils/deliveryDateRange';
import {
    getAddressForPointAction,
    getPointActionDeliveryStatusOutcomes,
    normalizeRoutePointAction,
    ROUTE_POINT_ACTION,
    ROUTE_POINT_ACTION_OPTIONS,
} from './routePointAction';
import RouteCustomerFilter from './RouteCustomerFilter';
import {
    TwoColumnGrid,
    Panel,
    PanelHeader,
    PanelBody,
    ListHeaderRow,
    DeliveryRow,
    RowTop,
    RowRight,
    ListRowActionCell,
    AddToMapButton,
    StatusCell,
    RowToggle,
    IdCell,
    CustomerCell,
    ExpandedDetail,
    MapPanelBody,
    MapEmpty,
    RouteStopsColumn,
    StopCard,
    StopOrderBadge,
    StopCardBody,
    StopTitle,
    StopMeta,
    StopActionFieldGroup,
    StopActionLabel,
    StopPointActionSelect,
    StopSecondaryActions,
    StopDescriptionSection,
    StopDescriptionHint,
    StopDescriptionTextarea,
    StopActions,
    ConnectorLine,
    IntroText,
    StopMotionWrap,
    StopPointActionDeliveryHint,
    RouteFooterActions,
} from './RotasPage.styles';

const { Button, FormGroup, Input, InputGroup, FiltersContainer, FilterGroup, PaginationContainer, PageInfo, PageButtons } = CrudStyles;

function formatScheduledDate(value) {
    if (!value) return null;
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return null;
    }
}

function generateStopId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `stop-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

const ROUTE_ALIAS_MAX_LENGTH = 60;
const ROUTE_ALIAS_COUNTER_THRESHOLD = 50;

function PointActionDeliveryStatusHint({ action }) {
    const outcomes = getPointActionDeliveryStatusOutcomes(action);

    if (outcomes.kind === 'in_transit') {
        return (
            <StopPointActionDeliveryHint>
                Status da entrega após conclusão do ponto:{' '}
                <DeliveryStatusBadge state={outcomes.status} compact /> (sucesso ou falha).
            </StopPointActionDeliveryHint>
        );
    }

    return (
        <StopPointActionDeliveryHint>
            Status da entrega após conclusão do ponto:{' '}
            <DeliveryStatusBadge state={outcomes.onSuccess} compact /> (sucesso) ou{' '}
            <DeliveryStatusBadge state={outcomes.onFailure} compact /> (falha).
        </StopPointActionDeliveryHint>
    );
}

function mapRoutePointsToStops(points) {
    return [...(points || [])]
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((point) => {
            const deliveryDetails = getPointDeliveryDetails(point);
            return {
                stopId: String(point.id),
                pointId: point.id,
                deliveryId: deliveryDetails?.id,
                pointAction: normalizeRoutePointAction(point.targetAction?.value),
                description: point.description ?? '',
            };
        })
        .filter((stop) => stop.deliveryId != null);
}

function syncPersistedSnapshotFromStops(stops, savedDescriptionsRef) {
    savedDescriptionsRef.current = new Map(
        (stops || []).map((stop) => [stop.stopId, stop.description ?? '']),
    );
}

function syncPersistedRouteMeta(data, savedAliasRef, savedDriverRef) {
    savedAliasRef.current = data.alias ?? '';
    savedDriverRef.current = {
        driverId: data.driverId ?? null,
        driverName: data.driverName ?? '',
    };
}

function mergeDeliveriesFromPoints(existingDeliveries, points) {
    const byId = new Map((existingDeliveries || []).map((delivery) => [delivery.id, delivery]));
    (points || []).forEach((point) => {
        const details = getPointDeliveryDetails(point);
        const shape = mapRouteDeliveryToPlanningShape(details);
        if (shape && !byId.has(shape.id)) {
            byId.set(shape.id, shape);
        }
    });
    return Array.from(byId.values());
}

function buildRouteSyncKey(routeData) {
    if (!routeData) return '';
    const pointIds = (routeData.points || []).map((point) => point.id).join(',');
    return `${routeData.id}:${routeData.lastUpdateDateTime ?? routeData.creationDateTime ?? ''}:${pointIds}`;
}

function hydrateEditorFromRoute(data, {
    savedAliasRef,
    savedDriverRef,
    savedDescriptionsRef,
    setRouteAlias,
    setDriverId,
    setDriverName,
    setStops,
    setDeliveries,
}) {
    const mappedStops = mapRoutePointsToStops(data.points);
    syncPersistedRouteMeta(data, savedAliasRef, savedDriverRef);
    syncPersistedSnapshotFromStops(mappedStops, savedDescriptionsRef);
    setRouteAlias((data.alias ?? '').slice(0, ROUTE_ALIAS_MAX_LENGTH));
    setDriverId(data.driverId ?? null);
    setDriverName(data.driverName ?? '');
    setStops(mappedStops);
    setDeliveries((prev) => mergeDeliveriesFromPoints(prev, data.points));
}

/**
 * @param {{
 *   mode: 'create' | 'edit',
 *   routeId?: string|number,
 *   routeData?: object|null,
 *   onCreated?: (id: number) => void,
 *   onRouteChange?: (update: object) => void,
 * }} props
 */
export default function RouteEditor({ mode, routeId, routeData = null, onCreated, onRouteChange }) {
    const isEditMode = mode === 'edit';

    const [deliveries, setDeliveries] = useState([]);
    const [deliveriesLoading, setDeliveriesLoading] = useState(true);
    const [deliveriesError, setDeliveriesError] = useState(null);
    const [deliveryFilters, setDeliveryFilters] = useState(() => ({
        id: '',
        customerId: null,
        customerName: '',
        ...getDefaultDeliveryDateRange(),
    }));
    const [appliedDeliveryFilters, setAppliedDeliveryFilters] = useState(() => ({
        id: '',
        customerId: null,
        customerName: '',
        ...getDefaultDeliveryDateRange(),
    }));
    const [deliveryFilterError, setDeliveryFilterError] = useState(null);
    const [deliveryPagination, setDeliveryPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [pointBusy, setPointBusy] = useState(false);
    const [aliasSaving, setAliasSaving] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [openDescriptionStopId, setOpenDescriptionStopId] = useState(null);
    const [stops, setStops] = useState([]);
    const [driverId, setDriverId] = useState(null);
    const [driverName, setDriverName] = useState('');
    const [routeAlias, setRouteAlias] = useState('');
    const [messageModal, setMessageModal] = useState({ open: false, title: '', message: '' });
    const stopWrapRefs = useRef({});
    const flipBeforeRectsRef = useRef(null);
    const stopsRef = useRef(stops);
    stopsRef.current = stops;
    /** Últimos valores persistidos com sucesso — base para rollback em falhas de API. */
    const savedAliasRef = useRef('');
    const savedDriverRef = useRef({ driverId: null, driverName: '' });
    const savedDescriptionsRef = useRef(new Map());
    const lastRouteSyncKeyRef = useRef('');

    const showError = useCallback((title, err) => {
        setMessageModal({
            open: true,
            title,
            message: getFriendlyErrorMessage(err) || 'Não foi possível concluir a operação.',
        });
    }, []);

    useEffect(() => {
        if (!isEditMode || !routeData) return;

        const syncKey = buildRouteSyncKey(routeData);
        if (lastRouteSyncKeyRef.current === syncKey) return;
        lastRouteSyncKeyRef.current = syncKey;

        hydrateEditorFromRoute(routeData, {
            savedAliasRef,
            savedDriverRef,
            savedDescriptionsRef,
            setRouteAlias,
            setDriverId,
            setDriverName,
            setStops,
            setDeliveries,
        });
    }, [isEditMode, routeData]);

    const fetchDeliveries = useCallback(async (page, filters) => {
        setDeliveriesLoading(true);
        setDeliveriesError(null);
        try {
            const result = await deliveryService.list({
                page,
                size: MAX_DELIVERY_PAGE_SIZE,
                sort: 'scheduledDeliveryDateTime,asc',
                state: ROUTE_PLANNING_DELIVERY_STATES,
                id: filters.id || undefined,
                customerId: filters.customerId || undefined,
                startDate: filters.startDate,
                endDate: filters.endDate,
            });
            setDeliveries(result.content || []);
            const pageMeta = result.page || {};
            setDeliveryPagination({
                page: pageMeta.number ?? page,
                totalPages: pageMeta.totalPages ?? 0,
                first: pageMeta.first ?? page === 0,
                last: pageMeta.last ?? true,
            });
        } catch (err) {
            setDeliveries([]);
            setDeliveryPagination({ page: 0, totalPages: 0, first: true, last: true });
            setDeliveriesError(
                getFriendlyErrorMessage(err) || 'Não foi possível carregar as entregas.',
            );
        } finally {
            setDeliveriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDeliveries(0, appliedDeliveryFilters);
    }, [fetchDeliveries, appliedDeliveryFilters]);

    const handleDeliveryFilterChange = useCallback((event) => {
        const { name, value } = event.target;
        setDeliveryFilterError(null);
        setDeliveryFilters((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleCustomerSelect = useCallback((customer) => {
        setDeliveryFilterError(null);
        setDeliveryFilters((prev) => ({
            ...prev,
            customerId: customer?.id ?? null,
            customerName: customer?.name ?? '',
        }));
    }, []);

    const handleDeliverySearch = useCallback(() => {
        const validation = validateDeliveryDateRange(deliveryFilters.startDate, deliveryFilters.endDate);
        if (!validation.valid) {
            setDeliveryFilterError(validation.message);
            return;
        }
        setDeliveryFilterError(null);
        setAppliedDeliveryFilters({ ...deliveryFilters });
    }, [deliveryFilters]);

    const handleDeliveryPageChange = useCallback(
        (newPage) => {
            fetchDeliveries(newPage, appliedDeliveryFilters);
        },
        [fetchDeliveries, appliedDeliveryFilters],
    );

    const deliveryById = useCallback(
        (id) => deliveries.find((delivery) => delivery.id === id),
        [deliveries],
    );

    const persistRouteMeta = useCallback(
        async (payload, metaUpdate) => {
            if (!isEditMode || routeId == null) return;
            await routeService.update(routeId, payload);
            onRouteChange?.(metaUpdate ?? payload);
        },
        [isEditMode, routeId, onRouteChange],
    );

    const handleAliasChange = useCallback((event) => {
        setRouteAlias(event.target.value.slice(0, ROUTE_ALIAS_MAX_LENGTH));
    }, []);

    const handleAliasSave = useCallback(async () => {
        if (!isEditMode) return;

        const nextAlias = (routeAlias ?? '').trim().slice(0, ROUTE_ALIAS_MAX_LENGTH);
        const previousAlias = savedAliasRef.current ?? '';
        if (nextAlias === previousAlias.trim()) return;

        setAliasSaving(true);
        try {
            await persistRouteMeta(
                { alias: nextAlias || null },
                { alias: nextAlias || null },
            );
            savedAliasRef.current = nextAlias;
            setRouteAlias(nextAlias);
        } catch (err) {
            setRouteAlias(previousAlias);
            showError('Erro ao salvar apelido', err);
        } finally {
            setAliasSaving(false);
        }
    }, [isEditMode, routeAlias, persistRouteMeta, showError]);

    const handleDriverChange = useCallback(
        async ({ driverId: nextDriverId, driverName: nextDriverName }) => {
            const previousDriver = savedDriverRef.current;

            setDriverId(nextDriverId);
            setDriverName(nextDriverName ?? '');
            if (!isEditMode) return;

            const normalizedDriverId =
                nextDriverId != null && !Number.isNaN(Number(nextDriverId))
                    ? Number(nextDriverId)
                    : null;

            const isRemovingDriver = normalizedDriverId == null;
            const hadDriver =
                previousDriver.driverId != null && !Number.isNaN(Number(previousDriver.driverId));

            if (isRemovingDriver && !hadDriver) return;
            if (
                !isRemovingDriver &&
                normalizedDriverId === Number(previousDriver.driverId)
            ) {
                return;
            }

            try {
                await persistRouteMeta(
                    isRemovingDriver ? { removeDriver: true } : { driverId: normalizedDriverId },
                    { driverId: normalizedDriverId, driverName: nextDriverName ?? '' },
                );
                savedDriverRef.current = {
                    driverId: nextDriverId ?? null,
                    driverName: nextDriverName ?? '',
                };
            } catch (err) {
                setDriverId(previousDriver.driverId);
                setDriverName(previousDriver.driverName ?? '');
                showError('Erro ao salvar motorista', err);
            }
        },
        [isEditMode, persistRouteMeta, showError],
    );

    const addStopForDelivery = useCallback(
        async (deliveryId) => {
            if (!deliveryById(deliveryId)) return;

            if (!isEditMode) {
                setStops((prev) => [
                    ...prev,
                    {
                        stopId: generateStopId(),
                        deliveryId,
                        pointAction: ROUTE_POINT_ACTION.DELIVERY,
                        description: '',
                    },
                ]);
                return;
            }

            setPointBusy(true);
            try {
                const { pointId } = await routeService.addPoint(routeId, {
                    deliveryId: Number(deliveryId),
                    action: ROUTE_POINT_ACTION.DELIVERY,
                    index: stopsRef.current.length + 1,
                    description: null,
                });

                if (pointId == null) {
                    throw new Error('O servidor não retornou o identificador do ponto.');
                }

                setStops((prev) => [
                    ...prev,
                    {
                        stopId: String(pointId),
                        pointId,
                        deliveryId,
                        pointAction: ROUTE_POINT_ACTION.DELIVERY,
                        description: '',
                    },
                ]);
                savedDescriptionsRef.current.set(String(pointId), '');
            } catch (err) {
                showError('Erro ao adicionar ponto', err);
            } finally {
                setPointBusy(false);
            }
        },
        [deliveryById, isEditMode, routeId, showError],
    );

    const removeStopById = useCallback(
        async (stopId) => {
            if (!isEditMode) {
                setStops((prev) => prev.filter((stop) => stop.stopId !== stopId));
                setOpenDescriptionStopId((open) => (open === stopId ? null : open));
                return;
            }

            const stop = stopsRef.current.find((item) => item.stopId === stopId);
            if (!stop?.pointId) return;

            const previousStops = stopsRef.current;
            const previousOpenDescriptionStopId = openDescriptionStopId;

            setStops((prev) => prev.filter((item) => item.stopId !== stopId));
            setOpenDescriptionStopId((open) => (open === stopId ? null : open));

            setPointBusy(true);
            try {
                await routeService.removePoint(routeId, stop.pointId);
                savedDescriptionsRef.current.delete(stopId);
            } catch (err) {
                setStops(previousStops);
                setOpenDescriptionStopId(previousOpenDescriptionStopId);
                showError('Erro ao remover ponto', err);
            } finally {
                setPointBusy(false);
            }
        },
        [isEditMode, routeId, openDescriptionStopId, showError],
    );

    const handleRowToggle = useCallback((deliveryId) => {
        setExpandedId((prev) => (prev === deliveryId ? null : deliveryId));
    }, []);

    const persistPointUpdate = useCallback(
        async (stop, pointAction, description) => {
            if (!isEditMode || !stop?.pointId) return;
            await routeService.updatePoint(routeId, stop.pointId, {
                action: normalizeRoutePointAction(pointAction),
                description: (description ?? '').trim() || null,
            });
        },
        [isEditMode, routeId],
    );

    const setPointActionByStopId = useCallback(
        async (stopId, pointAction) => {
            const stop = stopsRef.current.find((item) => item.stopId === stopId);
            if (!stop) return;

            const previousAction = stop.pointAction;

            setStops((prev) =>
                prev.map((item) => (item.stopId === stopId ? { ...item, pointAction } : item)),
            );

            if (!isEditMode) return;

            setPointBusy(true);
            try {
                await persistPointUpdate(
                    { ...stop, pointAction },
                    pointAction,
                    stop.description,
                );
            } catch (err) {
                setStops((prev) =>
                    prev.map((item) =>
                        item.stopId === stopId ? { ...item, pointAction: previousAction } : item,
                    ),
                );
                showError('Erro ao atualizar ação do ponto', err);
            } finally {
                setPointBusy(false);
            }
        },
        [isEditMode, persistPointUpdate, showError],
    );

    const setStopDescription = useCallback((stopId, description) => {
        setStops((prev) => prev.map((stop) => (stop.stopId === stopId ? { ...stop, description } : stop)));
    }, []);

    const saveStopDescription = useCallback(
        async (stopId) => {
            if (!isEditMode) return;
            const stop = stopsRef.current.find((item) => item.stopId === stopId);
            if (!stop?.pointId) return;

            const previousDescription = savedDescriptionsRef.current.get(stopId) ?? '';

            setPointBusy(true);
            try {
                await persistPointUpdate(stop, stop.pointAction, stop.description);
                savedDescriptionsRef.current.set(stopId, stop.description ?? '');
            } catch (err) {
                setStops((prev) =>
                    prev.map((item) =>
                        item.stopId === stopId ? { ...item, description: previousDescription } : item,
                    ),
                );
                showError('Erro ao salvar descrição', err);
            } finally {
                setPointBusy(false);
            }
        },
        [isEditMode, persistPointUpdate, showError],
    );

    const toggleDescriptionPanel = useCallback(
        async (stopId) => {
            const wasOpen = openDescriptionStopId === stopId;
            setOpenDescriptionStopId((prev) => (prev === stopId ? null : stopId));
            if (wasOpen && isEditMode) {
                await saveStopDescription(stopId);
            }
        },
        [openDescriptionStopId, isEditMode, saveStopDescription],
    );

    const handleStopMotionTransitionEnd = useCallback((e) => {
        if (e.propertyName !== 'transform') return;
        if (e.target !== e.currentTarget) return;
        e.currentTarget.style.transition = '';
        e.currentTarget.style.transform = '';
    }, []);

    const moveStop = useCallback(
        async (index, delta) => {
            const currentStops = stopsRef.current;
            const targetIndex = index + delta;
            if (targetIndex < 0 || targetIndex >= currentStops.length) return;

            const before = {};
            currentStops.forEach((stop) => {
                const el = stopWrapRefs.current[stop.stopId];
                if (el) before[stop.stopId] = el.getBoundingClientRect();
            });
            flipBeforeRectsRef.current = before;

            const previousStops = currentStops;
            const swappedStops = [...currentStops];
            [swappedStops[index], swappedStops[targetIndex]] = [
                swappedStops[targetIndex],
                swappedStops[index],
            ];
            setStops(swappedStops);

            if (!isEditMode) return;

            const currentStop = currentStops[index];
            const targetStop = currentStops[targetIndex];
            if (!currentStop?.pointId || !targetStop?.pointId) {
                setStops(previousStops);
                return;
            }

            setPointBusy(true);
            try {
                await routeService.swapPointOrder(routeId, currentStop.pointId, targetStop.pointId);
            } catch (err) {
                setStops(previousStops);
                showError('Erro ao reordenar pontos', err);
            } finally {
                setPointBusy(false);
            }
        },
        [isEditMode, routeId, showError],
    );

    const stopsOrderKey = useMemo(() => stops.map((stop) => stop.stopId).join(','), [stops]);
    const aliasLength = (routeAlias ?? '').length;
    const aliasAtLimit = aliasLength >= ROUTE_ALIAS_MAX_LENGTH;
    const showAliasCounter = aliasLength >= ROUTE_ALIAS_COUNTER_THRESHOLD;
    const aliasDirty = (routeAlias ?? '').trim() !== (savedAliasRef.current ?? '').trim();

    useLayoutEffect(() => {
        const beforeMap = flipBeforeRectsRef.current;
        if (!beforeMap || Object.keys(beforeMap).length === 0) return;
        flipBeforeRectsRef.current = null;

        stopsRef.current.forEach((stop) => {
            const el = stopWrapRefs.current[stop.stopId];
            const br = beforeMap[stop.stopId];
            if (!el || !br) return;
            const ar = el.getBoundingClientRect();
            const dy = br.top - ar.top;
            if (Math.abs(dy) < 0.5) return;

            el.style.transform = `translateY(${dy}px)`;
            el.style.transition = 'none';

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    el.style.transition = 'transform 0.32s cubic-bezier(0.25, 0.82, 0.2, 1)';
                    el.style.transform = 'translateY(0)';
                });
            });
        });
    }, [stopsOrderKey]);

    const handleCreateRoute = useCallback(async () => {
        if (stops.length === 0) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Adicione pelo menos uma parada ao roteiro antes de criar.',
            });
            return;
        }
        if (driverId == null || Number.isNaN(Number(driverId))) {
            setMessageModal({
                open: true,
                title: 'Aviso',
                message: 'Selecione um motorista para a rota.',
            });
            return;
        }

        setSubmitting(true);
        try {
            const items = stops.map((stop, index) => ({
                deliveryId: Number(stop.deliveryId),
                action: normalizeRoutePointAction(stop.pointAction ?? stop.stopKind),
                index: index + 1,
                description: (stop.description ?? '').trim() || null,
            }));

            const alias = (routeAlias ?? '').trim().slice(0, ROUTE_ALIAS_MAX_LENGTH) || null;

            const { id } = await routeService.create({
                items,
                driverId: Number(driverId),
                alias,
            });

            if (id == null) {
                throw new Error('O servidor não retornou o identificador do roteiro.');
            }

            onCreated?.(id);
        } catch (err) {
            showError('Erro ao criar roteiro', err);
        } finally {
            setSubmitting(false);
        }
    }, [stops, driverId, routeAlias, onCreated, showError]);

    const controlsDisabled = submitting || pointBusy || deliveriesLoading;

    if (isEditMode && !routeData) {
        return (
            <p style={{ margin: 0, color: '#b91c1c', fontSize: 14 }}>
                Não foi possível carregar os dados do roteiro para edição.
            </p>
        );
    }

    return (
        <>
            <IntroText>
                Use <strong>Adicionar</strong> para criar uma parada (pode repetir a mesma entrega quantas
                vezes precisar). Em cada parada, selecione a <strong>ação</strong> (entrega, coleta, retirada ou
                transferência), opcionalmente uma <strong>descrição</strong> para o motorista, e clique em ID ou
                cliente à esquerda para ver endereços e destinatário.
                {isEditMode ? (
                    <>
                        {' '}
                        Paradas e motorista são salvos automaticamente; use <strong>Atualizar nome</strong> para o
                        apelido da rota. Quando terminar, use <strong>Planejar roteiro</strong> no topo da página.
                    </>
                ) : null}
            </IntroText>

            <TwoColumnGrid>
                <Panel>
                    <PanelHeader>Entregas pendentes</PanelHeader>
                    <PanelBody>
                        <FiltersContainer style={{ margin: 0, boxShadow: 'none', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
                            <FilterGroup style={{ flex: '1 1 6rem', maxWidth: '8rem' }}>
                                <label htmlFor="route-delivery-id">Entrega</label>
                                <Input
                                    id="route-delivery-id"
                                    type="number"
                                    name="id"
                                    min="1"
                                    placeholder="Nº"
                                    value={deliveryFilters.id}
                                    onChange={handleDeliveryFilterChange}
                                />
                            </FilterGroup>
                            <FilterGroup style={{ flex: '1 1 9rem' }}>
                                <label htmlFor="route-delivery-start">Criação de</label>
                                <Input
                                    id="route-delivery-start"
                                    type="date"
                                    name="startDate"
                                    value={deliveryFilters.startDate}
                                    onChange={handleDeliveryFilterChange}
                                />
                            </FilterGroup>
                            <FilterGroup style={{ flex: '1 1 9rem' }}>
                                <label htmlFor="route-delivery-end">Criação até</label>
                                <Input
                                    id="route-delivery-end"
                                    type="date"
                                    name="endDate"
                                    value={deliveryFilters.endDate}
                                    onChange={handleDeliveryFilterChange}
                                />
                            </FilterGroup>
                            <FilterGroup style={{ flex: '1 1 12rem', minWidth: '10rem' }}>
                                <RouteCustomerFilter
                                    selectedCustomerId={deliveryFilters.customerId}
                                    selectedCustomerName={deliveryFilters.customerName}
                                    onSelect={handleCustomerSelect}
                                    disabled={deliveriesLoading}
                                />
                            </FilterGroup>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={handleDeliverySearch}
                                disabled={deliveriesLoading}
                                style={{ height: '38px' }}
                            >
                                Pesquisar
                            </Button>
                        </FiltersContainer>
                        {deliveryFilterError ? (
                            <p style={{ margin: 0, padding: '8px 16px', color: '#b91c1c', fontSize: 13 }}>
                                {deliveryFilterError}
                            </p>
                        ) : (
                            <p style={{ margin: 0, padding: '8px 16px', color: '#6b7280', fontSize: 12 }}>
                                Entregas elegíveis no intervalo selecionado: pendente, em trânsito, falha na entrega ou reagendada.
                                {appliedDeliveryFilters.customerId ? (
                                    <>
                                        {' '}
                                        Filtrando por cliente:{' '}
                                        <strong>
                                            {appliedDeliveryFilters.customerName ||
                                                `#${appliedDeliveryFilters.customerId}`}
                                        </strong>
                                        .
                                    </>
                                ) : null}
                            </p>
                        )}
                        <ListHeaderRow>
                            <span>Ação</span>
                            <span>ID</span>
                            <span>Cliente</span>
                            <span>Status</span>
                        </ListHeaderRow>
                        {deliveriesLoading ? (
                            <p style={{ margin: 0, padding: '24px 16px', color: '#6b7280', fontSize: 14 }}>
                                Carregando entregas…
                            </p>
                        ) : deliveriesError ? (
                            <p style={{ margin: 0, padding: '24px 16px', color: '#b91c1c', fontSize: 14 }}>
                                {deliveriesError}
                            </p>
                        ) : deliveries.length === 0 ? (
                            <p style={{ margin: 0, padding: '24px 16px', color: '#6b7280', fontSize: 14 }}>
                                Nenhuma entrega disponível para o roteiro.
                            </p>
                        ) : null}
                        {!deliveriesLoading &&
                            !deliveriesError &&
                            deliveries.map((delivery) => {
                                const expanded = expandedId === delivery.id;
                                return (
                                    <DeliveryRow key={delivery.id}>
                                        <RowTop>
                                            <ListRowActionCell>
                                                <AddToMapButton
                                                    onClick={() => addStopForDelivery(delivery.id)}
                                                    disabled={controlsDisabled}
                                                    title={`Adicionar entrega ${delivery.id} ao mapa (nova parada)`}
                                                >
                                                    Adicionar
                                                </AddToMapButton>
                                            </ListRowActionCell>
                                            <RowRight>
                                                <RowToggle
                                                    type="button"
                                                    $active={expanded}
                                                    onClick={() => handleRowToggle(delivery.id)}
                                                    aria-expanded={expanded}
                                                >
                                                    <IdCell>#{delivery.id}</IdCell>
                                                    <CustomerCell title={delivery.customerName}>
                                                        {delivery.customerName || '—'}
                                                        {formatScheduledDate(delivery.expectedDate) ? (
                                                            <span
                                                                style={{
                                                                    display: 'block',
                                                                    fontSize: 11,
                                                                    color: '#6b7280',
                                                                    fontWeight: 400,
                                                                    marginTop: 2,
                                                                }}
                                                            >
                                                                Agendada: {formatScheduledDate(delivery.expectedDate)}
                                                            </span>
                                                        ) : null}
                                                    </CustomerCell>
                                                </RowToggle>
                                                <StatusCell>
                                                    <DeliveryStatusBadge state={delivery.status} compact />
                                                </StatusCell>
                                            </RowRight>
                                        </RowTop>
                                        {expanded ? (
                                            <ExpandedDetail>
                                                <div>
                                                    <strong>Endereço de coleta:</strong>{' '}
                                                    {delivery.pickupAddress || '—'}
                                                </div>
                                                <div>
                                                    <strong>Endereço de entrega:</strong>{' '}
                                                    {delivery.deliveryAddress || '—'}
                                                </div>
                                                <div>
                                                    <strong>Destinatário:</strong>{' '}
                                                    {delivery.recipientName || '—'}
                                                </div>
                                            </ExpandedDetail>
                                        ) : null}
                                    </DeliveryRow>
                                );
                            })}
                        {!deliveriesLoading && !deliveriesError && deliveryPagination.totalPages > 1 ? (
                            <PaginationContainer style={{ padding: '12px 16px', boxShadow: 'none' }}>
                                <PageInfo>
                                    Página {deliveryPagination.totalPages === 0 ? 0 : deliveryPagination.page + 1} de{' '}
                                    {deliveryPagination.totalPages || 0}
                                </PageInfo>
                                <PageButtons>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={deliveryPagination.totalPages === 0 || deliveryPagination.first}
                                        onClick={() => handleDeliveryPageChange(deliveryPagination.page - 1)}
                                    >
                                        Anterior
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={deliveryPagination.totalPages === 0 || deliveryPagination.last}
                                        onClick={() => handleDeliveryPageChange(deliveryPagination.page + 1)}
                                    >
                                        Próxima
                                    </Button>
                                </PageButtons>
                            </PaginationContainer>
                        ) : null}
                    </PanelBody>
                </Panel>

                <Panel>
                    <PanelHeader>Roteiro</PanelHeader>
                    <MapPanelBody>
                        {stops.length === 0 ? (
                            <MapEmpty>Nenhuma parada. Use &quot;Adicionar&quot; na listagem.</MapEmpty>
                        ) : (
                            <RouteStopsColumn>
                                {stops.map((stop, index) => {
                                    const delivery = deliveryById(stop.deliveryId);
                                    if (!delivery) return null;
                                    const action = normalizeRoutePointAction(
                                        stop.pointAction ?? stop.stopKind,
                                    );
                                    const desc = (stop.description ?? '').trim();
                                    const { address: addressForLeg, title: addressTitle } =
                                        getAddressForPointAction(action, delivery);
                                    const descOpen = openDescriptionStopId === stop.stopId;
                                    return (
                                        <StopMotionWrap
                                            key={stop.stopId}
                                            ref={(el) => {
                                                if (el) stopWrapRefs.current[stop.stopId] = el;
                                                else delete stopWrapRefs.current[stop.stopId];
                                            }}
                                            onTransitionEnd={handleStopMotionTransitionEnd}
                                        >
                                            <StopCard>
                                                <StopOrderBadge aria-hidden>{index + 1}</StopOrderBadge>
                                                <StopCardBody>
                                                    <StopTitle>
                                                        #{delivery.id} — {delivery.customerName || '—'}
                                                    </StopTitle>
                                                    <StopMeta title={`${addressTitle}: ${addressForLeg}`}>
                                                        {addressForLeg}
                                                    </StopMeta>
                                                    {desc && !descOpen ? (
                                                        <StopDescriptionHint title={desc}>
                                                            {desc.length > 120 ? `${desc.slice(0, 120)}…` : desc}
                                                        </StopDescriptionHint>
                                                    ) : null}
                                                    <StopActionFieldGroup
                                                        role="group"
                                                        aria-label={`Parada ${index + 1}: ação do ponto`}
                                                    >
                                                        <StopActionLabel
                                                            htmlFor={`route-point-action-${stop.stopId}`}
                                                        >
                                                            Ação do ponto
                                                        </StopActionLabel>
                                                        <StopPointActionSelect
                                                            id={`route-point-action-${stop.stopId}`}
                                                            value={action}
                                                            disabled={controlsDisabled}
                                                            onChange={(e) =>
                                                                setPointActionByStopId(
                                                                    stop.stopId,
                                                                    e.target.value,
                                                                )
                                                            }
                                                            aria-label={`Ação da parada ${index + 1}`}
                                                        >
                                                            {ROUTE_POINT_ACTION_OPTIONS.map((opt) => (
                                                                <option
                                                                    key={opt.value}
                                                                    value={opt.value}
                                                                    title={opt.description}
                                                                >
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </StopPointActionSelect>
                                                        <PointActionDeliveryStatusHint action={action} />
                                                    </StopActionFieldGroup>
                                                    <StopSecondaryActions>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            disabled={controlsDisabled}
                                                            onClick={() => toggleDescriptionPanel(stop.stopId)}
                                                            aria-expanded={descOpen}
                                                        >
                                                            {desc ? 'Editar descrição' : 'Descrição'}
                                                        </Button>
                                                    </StopSecondaryActions>
                                                    {descOpen ? (
                                                        <StopDescriptionSection>
                                                            <StopDescriptionHint>
                                                                Opcional — observações para o motorista neste
                                                                ponto.
                                                            </StopDescriptionHint>
                                                            <StopDescriptionTextarea
                                                                value={stop.description ?? ''}
                                                                disabled={controlsDisabled}
                                                                onChange={(e) =>
                                                                    setStopDescription(stop.stopId, e.target.value)
                                                                }
                                                                onBlur={() => {
                                                                    if (isEditMode) {
                                                                        saveStopDescription(stop.stopId);
                                                                    }
                                                                }}
                                                                placeholder="Ex.: portaria B, agendar com recebimento…"
                                                                aria-label={`Descrição da parada ${index + 1}`}
                                                            />
                                                        </StopDescriptionSection>
                                                    ) : null}
                                                    <StopActions>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            disabled={controlsDisabled || index === 0}
                                                            onClick={() => moveStop(index, -1)}
                                                            title="Mover parada para cima"
                                                        >
                                                            ↑ Subir
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            disabled={
                                                                controlsDisabled || index === stops.length - 1
                                                            }
                                                            onClick={() => moveStop(index, 1)}
                                                            title="Mover parada para baixo"
                                                        >
                                                            ↓ Descer
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            disabled={controlsDisabled}
                                                            onClick={() => removeStopById(stop.stopId)}
                                                            title="Remover esta parada da rota"
                                                        >
                                                            Remover
                                                        </Button>
                                                    </StopActions>
                                                </StopCardBody>
                                            </StopCard>
                                            {index < stops.length - 1 ? <ConnectorLine aria-hidden /> : null}
                                        </StopMotionWrap>
                                    );
                                })}
                            </RouteStopsColumn>
                        )}
                    </MapPanelBody>
                </Panel>
            </TwoColumnGrid>

            <FormGroup>
                <label htmlFor="route-alias">Apelido da rota</label>
                {isEditMode ? (
                    <InputGroup>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <Input
                                id="route-alias"
                                type="text"
                                value={routeAlias}
                                disabled={controlsDisabled || aliasSaving}
                                onChange={handleAliasChange}
                                placeholder="Ex.: Rota Centro SP — manhã"
                                maxLength={ROUTE_ALIAS_MAX_LENGTH}
                                autoComplete="off"
                                aria-describedby={
                                    aliasAtLimit || showAliasCounter ? 'route-alias-hint' : undefined
                                }
                            />
                        </div>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={handleAliasSave}
                            disabled={controlsDisabled || aliasSaving || !aliasDirty}
                        >
                            {aliasSaving ? 'Salvando…' : 'Atualizar nome'}
                        </Button>
                    </InputGroup>
                ) : (
                    <Input
                        id="route-alias"
                        type="text"
                        value={routeAlias}
                        disabled={controlsDisabled}
                        onChange={handleAliasChange}
                        placeholder="Ex.: Rota Centro SP — manhã"
                        maxLength={ROUTE_ALIAS_MAX_LENGTH}
                        autoComplete="off"
                        aria-describedby={
                            aliasAtLimit || showAliasCounter ? 'route-alias-hint' : undefined
                        }
                    />
                )}
                <IntroText>
                    {isEditMode
                        ? 'Opcional — clique em Atualizar nome para salvar o apelido.'
                        : 'Opcional — nome para identificar a rota na listagem.'}
                </IntroText>
                {aliasAtLimit ? (
                    <IntroText
                        id="route-alias-hint"
                        style={{ color: '#b45309', fontSize: 12 }}
                    >
                        Limite de {ROUTE_ALIAS_MAX_LENGTH} caracteres atingido.
                    </IntroText>
                ) : showAliasCounter ? (
                    <IntroText id="route-alias-hint" style={{ fontSize: 12 }}>
                        {aliasLength}/{ROUTE_ALIAS_MAX_LENGTH} caracteres
                    </IntroText>
                ) : null}
            </FormGroup>

            <DeliveryDriverSection
                localSelectionOnly
                sectionTitle="Motorista da rota"
                helpText={
                    isEditMode
                        ? 'Escolha o motorista responsável por esta rota. A seleção é salva automaticamente.'
                        : 'Escolha o motorista responsável por esta rota. Use o botão abaixo para abrir a lista paginada; é possível alterar ou remover a seleção no mesmo modal.'
                }
                driverId={driverId}
                driverName={driverName}
                onDriverChange={handleDriverChange}
            />

            {!isEditMode ? (
                <RouteFooterActions>
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleCreateRoute}
                        disabled={controlsDisabled}
                    >
                        {submitting ? 'Criando…' : 'Criar roteiro'}
                    </Button>
                </RouteFooterActions>
            ) : null}

            {messageModal.open ? (
                <MessageModal
                    title={messageModal.title}
                    message={messageModal.message}
                    onClose={() => setMessageModal((modal) => ({ ...modal, open: false }))}
                />
            ) : null}
        </>
    );
}
